import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { UseFormSetValue } from 'react-hook-form';
import { CmdError } from '@commander-services/gui-components';
import usePermissions from '../../../hooks/usePermissions';
import {
  PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN,
  PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
  PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
  PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
} from '../../../Services/PermissionsService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { CardError, CardItem, GridErrors, OnlineTemplateFormValues } from './types';
import useAnalytics from '../../../hooks/useAnalytics';

interface ICardNameInputProps {
  id: string;
  label: string;
  tooltip?: string;
  index: number;
  setValue: UseFormSetValue<OnlineTemplateFormValues>;
  name: string;
  value: CardItem[];
  error: CardError[];
  placeholder?: string;
  clearGridCallback: (index: number) => void;
  clearGridTooltip?: string;
  deleteCardCallback: (index: number) => void;
  deleteCardTooltip?: string;
  previewGridCallback: (index: number) => void;
  previewGridTooltip?: string;
  gridErrors: GridErrors | null;
}

function CardNameInput(props: ICardNameInputProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const errorMessage = props.error?.[props.index]?.name;
  const access = usePermissions(
    [
      PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
      PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
      PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
      PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN,
    ],
    selectedCustomers
  );

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.setValue(`cards.${props.index}.name`, event.currentTarget.value, {
      shouldValidate: true,
    });
  };

  const isPreviewDisabled = React.useMemo<boolean>(() => {
    if (
      props.gridErrors &&
      props.gridErrors[props.index] &&
      Object.keys(props.gridErrors[props.index]).length > 0
    ) {
      return true;
    }
    return false;
  }, [props.gridErrors, props.index]);

  return (
    <div className={`form-group ${errorMessage ? 'form-group-error' : ''}`}>
      <div className="row">
        <label className="col-12 col-lg-auto form-label">{props.label}</label>
        <div className="col col-lg-5">
          <input
            type="text"
            className={`form-control ${errorMessage ? 'err-border' : ''}`}
            name={props.name}
            id={props.id}
            title={props.tooltip || ''}
            placeholder={props.placeholder || ''}
            onChange={handleOnChange}
            value={props.value?.[props.index]?.name}
            disabled={access.isReadOnly || undefined}
          />
          {errorMessage && <CmdError message={errorMessage} />}
        </div>
        <div className="col-auto ml-auto align-self-center">
          <button
            className={`e-button e-button--square ${isPreviewDisabled ? 'e-button--disabled' : ''}`}
            disabled={isPreviewDisabled}
            type="button"
            title={props.previewGridTooltip || ''}
            onClick={() => {
              trackEvent(['Online template ', 'click', 'Preview card']);
              props.previewGridCallback(props.index);
            }}
          >
            <i className="cmd-icon cmd-icon-preview" />
            <span className="sr-only">{props.previewGridTooltip || ''}</span>
          </button>
          {(access.canCreate || access.canUpdate) && (
            <button
              type="button"
              className="e-button e-button--square"
              title={props.clearGridTooltip || ''}
              onClick={() => props.clearGridCallback(props.index)}
            >
              <i className="cmd-icon cmd-icon-clear" />
              <span className="sr-only">{props.clearGridTooltip || ''}</span>
            </button>
          )}
          {(access.canCreate || access.canUpdate) && (
            <button
              type="button"
              className={`e-button e-button--square ${
                props.value.length < 2 ? 'e-button--disabled' : ''
              }`}
              title={props.deleteCardTooltip || ''}
              onClick={
                props.value.length < 2 ? undefined : () => {
                  trackEvent(['Online template ', 'click', 'Delete card']);
                  props.deleteCardCallback(props.index)
                }
              }
            >
              <i className="cmd-icon cmd-icon-bin" />
              <span className="sr-only">{props.deleteCardTooltip || ''}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CardNameInput;
